<script lang="ts">
  import Modal from '../modal/Modal.svelte'
  import Button from '../button/Button.svelte'
  import i18n from '../../i18n'
  import { CloudUpload } from '@lucide/svelte'
  import { printers } from '../../constants/printers'
  import { setPreferredPrinter } from '../../stores/preferred-printer'
  import { supportedFileExtensionsHtmlAccept } from '../../constants/files'
  import Separator from '../separator/Separator.svelte'

  type Props = {
    open?: boolean
    onAddPrinter?: (model: PrinterModel) => void
    onImportFile?: (files: FileList | null) => void
  }

  let { open = $bindable(false), onAddPrinter, onImportFile }: Props = $props()

  let selectedModel: PrinterModel = $state(printers[0].model)

  function handleSubmit(e: SubmitEvent) {
    e.preventDefault()
    setPreferredPrinter(selectedModel)
    onAddPrinter?.(selectedModel)
    open = false
  }

  function handleImportFile(e: Event) {
    onImportFile?.((e.target as HTMLInputElement).files)
    open = false
  }
</script>

<Modal
  bind:open
  onsubmit={handleSubmit}
  title={$i18n.t('project.emptyProjectModal.title')}
>
  {#snippet body()}
    <div class="content">
      {#each printers as printer (printer.model)}
        <label class="item">
          <div class="item-info">
            <printer.icon />
            <div>
              <p class="name">{printer.name}</p>
              <p class="description">
                {$i18n.t(
                  `project.emptyProjectModal.descriptions.${printer.model}`,
                )}
              </p>
            </div>
          </div>
          <input
            class="radio-input"
            type="radio"
            name="printer-model"
            value={printer.model}
            bind:group={selectedModel}
          />
          <span class="radio"></span>
        </label>
      {/each}
    </div>
    <Separator />
    <label class="item">
      <div class="item-info">
        <CloudUpload />
        <div>
          <p class="name">
            {$i18n.t('project.emptyProjectModal.importFile.title')}
          </p>
          <p class="description">
            {$i18n.t('project.emptyProjectModal.importFile.description')}
          </p>
        </div>
      </div>
      <input
        class="import-file-input"
        accept={supportedFileExtensionsHtmlAccept}
        multiple
        onchange={handleImportFile}
        type="file"
      />
    </label>
  {/snippet}
  {#snippet footer()}
    <Button type="submit">
      {$i18n.t('project.emptyProjectModal.submit')}
    </Button>
  {/snippet}
</Modal>

<style lang="scss">
  .import-file-input {
    display: none;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .item {
    min-height: 5rem;
    border-radius: 0.25rem;
    border: 1px solid var(--dropdown-stroke);
    display: flex;
    align-items: center;
    padding: 0 1rem;
    justify-content: space-between;
    cursor: pointer;

    &:has(.radio-input:checked) {
      border-color: var(--primary);
    }

    &:has(.radio-input:focus-visible) .radio {
      outline: 2px solid var(--primary);
      outline-offset: 2px;
    }

    p.name {
      font-weight: 600;
      font-size: 1rem;
      line-height: 120%;
    }

    p.description {
      font-size: 0.75rem;
      color: var(--secondary-foreground);
    }

    &-info {
      display: flex;
      align-items: center;
      gap: 1rem;

      & :global(svg) {
        width: 1.5rem;
        height: 1.5rem;
      }
    }
  }

  .radio-input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  .radio {
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 100vmax;
    border: 1px solid var(--dropdown-stroke);
    flex-shrink: 0;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      inset: 4px;
      border-radius: 100vmax;
      background: transparent;
    }

    .item:has(.radio-input:checked) & {
      border-color: var(--foreground);

      &::after {
        background: var(--foreground);
      }
    }
  }
</style>
